import apiClient, { uploadFilesByType } from 'api/base';
import { GetAllItemsParams } from 'interfaces';
import { DraftRecognition, Recognition } from 'models/recognition.model';

export const getRecognitions = async ({
  status,
  isOwner,
}: GetAllItemsParams): Promise<Recognition[]> => {
  const queries = [];

  if (status) {
    queries.push(`status=${status}`);
  }
  if (isOwner) {
    queries.push(`isOwner=${isOwner}`);
  }

  const query = queries.join('&');
  const { data } = await apiClient.get(
    `/recognitions${query ? `?${query}` : ''}`,
  );

  return data;
};

export const draftRecognition = async (
  recognition: Omit<DraftRecognition, 'status'>,
): Promise<Recognition> => {
  const { data } = await apiClient.post('/recognitions', { ...recognition });

  return data;
};

export const getRecognition = async (id: string): Promise<Recognition> => {
  const { data } = await apiClient.get(`/recognitions/${id}`);

  return data;
};

export const uploadFiles = async (
  id: string,
  attachments: File[],
): Promise<Recognition> => {
  await uploadFilesByType(id, attachments, 'recognitions');

  const data = await getRecognition(id);

  return data;
};

export const updateRecognition = async (
  id: string,
  recognition: Omit<DraftRecognition, 'status'>,
): Promise<Recognition> => {
  const { data } = await apiClient.put(`/recognitions/${id}`, recognition);

  return data;
};

export const deleteRecognition = async (id: string): Promise<Recognition> => {
  const { data } = await apiClient.delete(`/recognitions/${id}`);

  return data;
};

export const reviewRecognition = async (id: string): Promise<Recognition> => {
  const { data } = await apiClient.patch(`/recognitions/${id}/review`);

  return data;
};

export const createRecognitionAndReview = async (
  recognition: Omit<DraftRecognition, 'status'>,
  attachments?: File[],
): Promise<Recognition> => {
  try {
    const updatedRecognition = await draftRecognition(recognition);

    if (attachments?.length) {
      await uploadFiles(updatedRecognition.id, attachments);
    }

    const data = await reviewRecognition(updatedRecognition.id);

    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const updateRecognitionAndReview = async (
  recognition: Omit<
    Recognition,
    'status' | 'author' | 'createdAt' | 'updatedAt'
  >,
): Promise<Recognition> => {
  try {
    const updatedRecognition = await updateRecognition(recognition.id, {
      ...recognition,
      ...(recognition.reward === 'PICTURE'
        ? { attachments: recognition.attachments?.filter((f) => f.id) || [] }
        : {}),
    });

    const notSavedAttachments =
      recognition.attachments?.filter((f) => !f.id) || [];

    if (recognition.reward === 'PICTURE' && notSavedAttachments?.length) {
      await uploadFiles(updatedRecognition.id, notSavedAttachments);
    }

    const data = await reviewRecognition(updatedRecognition.id);

    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const approveRecognition = async (id: string): Promise<Recognition> => {
  const { data } = await apiClient.patch(`/recognitions/${id}/approve`);

  return data;
};

export const rejectRecognition = async (id: string): Promise<Recognition> => {
  const { data } = await apiClient.patch(`/recognitions/${id}/reject`);

  return data;
};

export const reopenRecognition = async (id: string): Promise<Recognition> => {
  const { data } = await apiClient.patch(`/recognitions/${id}/reopen`);

  return data;
};
